import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Send, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OrderFormData, initialOrderData, getSteps } from '@/types/order';
import { useOrderPricing } from '@/hooks/useOrderPricing';
import { PricingSummary, PricingSummaryMobile } from '@/components/order/components/PricingSummary';

// Import des étapes
import { Step1Contexte } from '@/components/order/steps/Step1Contexte';
import { Step2Services } from '@/components/order/steps/Step2Services';
import { Step3Donneur } from '@/components/order/steps/Step3Donneur';
import { Step4Bien } from '@/components/order/steps/Step4Bien';
import { Step5Contact } from '@/components/order/steps/Step5Contact';
import { Step6Acces } from '@/components/order/steps/Step6Acces';
import { Step7Recapitulatif } from '@/components/order/steps/Step7Recapitulatif';

const URL_SERVICE_MAP: Record<string, keyof OrderFormData> = {
  visites: 'packVisites',
  marketing: 'packMarketing',
};

export default function Commander() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // État du formulaire avec les données initiales
  const [formData, setFormData] = useState<OrderFormData>(() => {
    // Pré-sélection via URL params
    const initial = { ...initialOrderData };
    const services = searchParams.getAll('service');
    services.forEach((service) => {
      const field = URL_SERVICE_MAP[service];
      if (field) {
        (initial as Record<string, unknown>)[field] = true;
        if (field === 'packMarketing') {
          initial.marketingType = 'bien';
        }
      }
    });
    return initial;
  });

  // Calcul du pricing en temps réel
  const pricing = useOrderPricing(formData);

  // Étapes dynamiques basées sur l'occupation et pack visites
  const steps = getSteps(formData.occupation, formData.packVisites);

  // Ajuster l'index si les steps changent dynamiquement
  useEffect(() => {
    if (currentStep >= steps.length) {
      setCurrentStep(steps.length - 1);
    }
  }, [steps.length, currentStep]);

  // Mise à jour des données
  const updateFormData = (data: Partial<OrderFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  // Navigation
  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Validation par étape
  const canGoToNextStep = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case 'contexte':
        return !!formData.typeVente;

      case 'services':
        // Au moins un pack sélectionné
        return formData.packVisites || formData.packMarketing ||
               formData.packControles.length > 0;

      case 'donneur':
        return !!(
          formData.societe &&
          formData.nomPrenom &&
          formData.email &&
          formData.telephone &&
          formData.adresseDonneurRue &&
          formData.adresseDonneurCP &&
          formData.adresseDonneurVille
        );

      case 'bien':
        return !!formData.adresseBien;

      case 'contact':
        // Si bien vide, toujours valide
        if (formData.occupation === 'vide') return true;
        // Sinon, nom et téléphone requis
        return !!(formData.occupantNom && formData.occupantTelephone);

      case 'acces':
        if (formData.modeAcces === 'code') {
          return !!formData.codeAcces;
        }
        if (formData.modeAcces === 'cles_ailleurs') {
          return !!(
            formData.adresseRetraitCles &&
            formData.contactRetraitClesNom &&
            formData.contactRetraitClesTelephone
          );
        }
        return true;

      case 'recap':
        return formData.confirmProfessionnel && formData.acceptConditions;

      default:
        return true;
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // En développement local, simuler l'envoi
      const isDev = import.meta.env.DEV;

      if (isDev) {
        // Mode développement : simuler un délai et afficher les données
        console.log('=== MODE DÉVELOPPEMENT ===');
        console.log('Données du formulaire:', formData);
        console.log('Pricing:', pricing);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Mode production : appel API Netlify Function
        const response = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ formData, pricing }),
        });

        if (!response.ok) {
          throw new Error('Erreur lors de l\'envoi');
        }
      }

      // Redirection vers la page de confirmation
      navigate('/confirmation', {
        state: { orderData: formData, pricing },
      });
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      alert('Une erreur est survenue lors de l\'envoi de votre commande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render de l'étape courante
  const renderStep = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case 'contexte':
        return <Step1Contexte data={formData} onChange={updateFormData} />;
      case 'services':
        return <Step2Services data={formData} onChange={updateFormData} />;
      case 'donneur':
        return <Step3Donneur data={formData} onChange={updateFormData} />;
      case 'bien':
        return <Step4Bien data={formData} onChange={updateFormData} />;
      case 'contact':
        return <Step5Contact data={formData} onChange={updateFormData} />;
      case 'acces':
        return <Step6Acces data={formData} onChange={updateFormData} />;
      case 'recap':
        return <Step7Recapitulatif data={formData} onChange={updateFormData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream">
      {/* Header */}
      <header className="bg-white border-b border-navy-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-navy-600 hover:text-gold-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Retour</span>
            </button>
            <span className="text-xl font-display font-bold text-navy-600">
              VISINOT<span className="text-gold-500">.</span>
            </span>
            <div className="w-24" /> {/* Spacer */}
          </div>
        </div>
      </header>

      {/* Indicateur d'étapes horizontal (mobile/tablet) */}
      <div className="bg-white border-b border-navy-100 py-6 lg:hidden">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-navy-600">
              Étape {currentStep + 1} sur {steps.length}
            </span>
            <span className="text-sm text-navy-400">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-navy-100 rounded-full h-2">
            <motion.div
              className="bg-gold-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-lg font-semibold text-navy-600 mt-4">
            {steps[currentStep].title}
          </p>
          <p className="text-sm text-navy-400">
            {steps[currentStep].description}
          </p>
        </div>
      </div>

      {/* Contenu principal avec sidebar */}
      <div className="container mx-auto px-6 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-8 items-start">
            {/* Indicateur d'étapes vertical (desktop) */}
            <div className="hidden lg:block w-64 flex-shrink-0 sticky top-24">
              <div className="bg-white rounded-2xl shadow-card p-6">
                <h3 className="font-semibold text-navy-600 mb-6">Progression</h3>
                <div className="space-y-4">
                  {steps.map((step, index) => {
                    const isCompleted = index < currentStep;
                    const isCurrent = index === currentStep;

                    return (
                      <div key={step.id} className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          isCompleted ? 'bg-gold-500' :
                          isCurrent ? 'bg-gold-500' :
                          'bg-navy-100'
                        }`}>
                          {isCompleted ? (
                            <Check className="w-4 h-4 text-white" />
                          ) : (
                            <span className={`text-sm font-semibold ${
                              isCurrent ? 'text-white' : 'text-navy-400'
                            }`}>
                              {index + 1}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            isCurrent ? 'text-navy-600' : 'text-navy-400'
                          }`}>
                            {step.title}
                          </p>
                          <p className="text-xs text-navy-300">{step.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pricing Summary Desktop */}
              <div className="mt-6">
                <PricingSummary pricing={pricing} />
              </div>
            </div>

            {/* Formulaire */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl shadow-card p-6 md:p-8 lg:p-12 mb-24 lg:mb-0">
                <AnimatePresence mode="wait">
                  <motion.div key={currentStep}>
                    {renderStep()}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation buttons */}
                <div className="flex items-center justify-between mt-12 pt-8 border-t border-navy-100">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={goToPreviousStep}
                    disabled={currentStep === 0}
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Précédent
                  </Button>

                  {currentStep < steps.length - 1 ? (
                    <Button
                      variant="gold"
                      size="lg"
                      onClick={goToNextStep}
                      disabled={!canGoToNextStep()}
                    >
                      Suivant
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  ) : (
                    <Button
                      variant="gold"
                      size="lg"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !canGoToNextStep()}
                    >
                      {isSubmitting ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          Envoyer la commande
                          <Send className="w-5 h-5" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Summary Mobile (sticky bottom) */}
      <PricingSummaryMobile pricing={pricing} />
    </div>
  );
}
